import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FanNewsStory, newsMetaLine } from '../fan-news/fan-news.model';
import { FanNewsStoryDialogComponent } from '../fan-news-story-dialog/fan-news-story-dialog.component';
import { FanNewsListDialogComponent } from '../fan-news-list-dialog/fan-news-list-dialog.component';

const SCROLL_PX_PER_SECOND = 40;
// Resume this long after the last manual scroll (wheel, touch or scrollbar drag), even if hovered.
const MANUAL_SCROLL_PAUSE_MS = 1500;
// Stories the list shows per design (popup = the v2 "Show" dialog); more than that overflows and
// gets the continuous ticker. v2 (redesigned Fan Pulse panel) has no inline list since US-19.
const VISIBLE_STORIES = { v1: 3, popup: 6 };

// "Latest News" card in the agent Fan Hub (specs/ureport-news-feed). Lists every story it is
// given in a scroll area about 3 stories tall (network page = network + schools, school page =
// that school only); renders nothing when there are none. When the stories overflow, the list
// scrolls continuously as a seamless loop (the stories are rendered twice; the copy is hidden from
// assistive tech), pausing on hover, keyboard focus, briefly after a manual scroll and while the tab
// is hidden, and never running for prefers-reduced-motion. design="v2" (specs/ai-agent-home-redesign
// US-19) shows only the heading and a Show button that opens the list in FanNewsListDialogComponent,
// which renders this card again with design="popup" (list only).
@Component({
  selector: 'app-fan-news-card',
  templateUrl: './fan-news-card.component.html',
  styleUrls: ['./fan-news-card.component.scss'],
})
export class FanNewsCardComponent implements OnChanges, OnDestroy {
  @Input() stories: FanNewsStory[] = [];
  @Input() design: 'v1' | 'v2' | 'popup' = 'v1';
  @Input() sourceName = '';
  list: ElementRef<HTMLElement>;

  // The list only exists while there are stories, so the scroll listener is (re)attached here.
  // Outside Angular: the ticker scrolls every frame and must not trigger change detection.
  @ViewChild('list') set listRef(ref: ElementRef<HTMLElement>) {
    this.list?.nativeElement.removeEventListener('scroll', this.onScroll);
    this.list = ref;
    this.zone.runOutsideAngular(() => ref?.nativeElement.addEventListener('scroll', this.onScroll, { passive: true }));
  }

  readonly metaLine = newsMetaLine;
  looping = false;
  private frame: number;
  private lastTime: number;
  private position = 0;
  private hovering = false;
  private keyboardFocus = false;
  private pausedUntil = 0;

  constructor(private dialog: MatDialog, private zone: NgZone) {}

  ngOnChanges(): void {
    this.stop();
    this.position = 0;
    if (this.list) {
      this.list.nativeElement.scrollTop = 0;
    }
    this.looping = this.design !== 'v2' && (this.stories?.length || 0) > VISIBLE_STORIES[this.design] && !prefersReducedMotion();
    if (this.looping) {
      // Outside Angular so animation frames never trigger change detection.
      this.zone.runOutsideAngular(() => (this.frame = requestAnimationFrame(t => this.onFrame(t))));
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.list?.nativeElement.removeEventListener('scroll', this.onScroll);
  }

  setHovering(hovering: boolean): void {
    this.hovering = hovering;
  }

  // Only keyboard focus pauses; focus left behind by a mouse click must not stop the ticker.
  setFocus(event: FocusEvent | null): void {
    this.keyboardFocus = !!(event?.target as Element)?.matches?.(':focus-visible');
  }

  // A scroll the ticker did not make (wheel, touch, scrollbar drag): pause briefly, then carry on
  // from where the user left it - also when the pointer is still over the list.
  private onScroll = (): void => {
    const el = this.list?.nativeElement;
    if (el && Math.abs(el.scrollTop - this.position) > 2) {
      this.hovering = false;
      this.pausedUntil = Date.now() + MANUAL_SCROLL_PAUSE_MS;
      this.position = el.scrollTop;
      el.style.setProperty('--fan-news-shift', '0px');
    }
  };

  // Moves the list by elapsedMs worth of scrolling; past the first copy it jumps back one copy's
  // height, which looks identical because the second copy follows it.
  step(elapsedMs: number): void {
    const el = this.list?.nativeElement;
    if (!el || this.hovering || this.keyboardFocus || Date.now() < this.pausedUntil || document.hidden) {
      return;
    }
    const firstCopy = el.querySelector<HTMLElement>('.fan-news-item--copy');
    const loopHeight = firstCopy?.offsetTop || 0;
    if (!loopHeight) {
      return;
    }
    this.position += (SCROLL_PX_PER_SECOND * elapsedMs) / 1000;
    if (this.position >= loopHeight) {
      this.position -= loopHeight;
    }
    // scrollTop snaps to whole device pixels, so under ~1px per frame the list would move in
    // uneven jumps (visible as lag). Scroll by whole device pixels and move the stories by the
    // remaining fraction with a compositor transform, so every frame advances evenly.
    const dpr = window.devicePixelRatio || 1;
    const whole = Math.floor(this.position * dpr) / dpr;
    el.scrollTop = whole;
    el.style.setProperty('--fan-news-shift', `${(whole - this.position).toFixed(3)}px`);
  }

  openStory(story: FanNewsStory): void {
    this.dialog.open(FanNewsStoryDialogComponent, {
      data: { story },
      width: '900px',
      maxWidth: '96vw',
      height: '86vh',
      panelClass: 'fan-news-dialog-panel',
    });
  }

  openList(): void {
    this.dialog.open(FanNewsListDialogComponent, {
      data: { stories: this.stories, sourceName: this.sourceName },
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '86vh',
      panelClass: 'agent-home-dialog-panel',
    });
  }

  trackById(_: number, story: FanNewsStory): string {
    return story.id;
  }

  private onFrame(time: number): void {
    // Cap the step so a long frame gap (e.g. a background tab) never jumps the list.
    this.step(this.lastTime ? Math.min(time - this.lastTime, 100) : 0);
    this.lastTime = time;
    this.frame = requestAnimationFrame(t => this.onFrame(t));
  }

  private stop(): void {
    this.list?.nativeElement.style.setProperty('--fan-news-shift', '0px');
    cancelAnimationFrame(this.frame);
    this.frame = undefined;
    this.lastTime = undefined;
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}
